/**
 * V2, two pens (docs/ideas/loom-v2.md, phase 3): Ink is what you do; Thread is what you make true. The pen switch sits
 * on the writing line, with a knot for each thread left (three a chapter); a Thread line is sent as "/weave …", set in
 * gold, and the world's pull (what the canon pass read it wove back) shows in rust under "the world pulls back". The
 * server holds the allowance (shared/story.ts weavesLeft); the switch only shows it. pens.css has the shared look.
 */
import { useEffect, useState } from "react";
import type { StoryUI } from "../useStory";
import { introDue, introSeen, returning, threadHintDue, threadHintSeen } from "./intro";
import "./pens.css";

/** Opens the short list at "The two pens" (App.tsx listens). */
export const helpOnPens = () => window.dispatchEvent(new CustomEvent("weftwake:help", { detail: "pens" }));

/** A span that acts as a button: the themes style every button in the writer as the round send button. */
export function Tap({ className, label, onTap, children }: { className: string; label?: string; onTap(): void; children: React.ReactNode }) {
  return (
    <span role="button" tabIndex={0} className={className} aria-label={label} title={label}
      onClick={onTap} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTap(); } }}>
      {children}
    </span>
  );
}

const WEAVE = /^\/weave\b\s*/i;
export const isThread = (input: string) => WEAVE.test(input);

/** The player's line as a theme shows it: a Thread line in gold, without its command. */
export function PenLine({ input }: { input: string }) {
  if (!isThread(input)) return <>{input}</>;
  return <span className="v2-thread"><span className="sr">Made true: </span>{input.replace(WEAVE, "")}</span>;
}

/** Ink or Thread. Spans, not buttons: the themes style every button in the writer as the round send button. */
export function PenSwitch({ s }: { s: StoryUI }) {
  const weave = s.view?.weave;
  if (!s.id || !weave || s.draft.trimStart().startsWith("/")) return null;
  const thread = s.pen === "thread";
  const can = weave.left > 0;
  const pick = (p: "ink" | "thread") => { if (p === "thread" && !can) return; if (p === "thread") introSeen(); s.setPen(p); };
  const key = (p: "ink" | "thread") => (e: React.KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(p); } };
  return (
    <p className="v2-pens" role="radiogroup" aria-label="Pen">
      <span role="radio" tabIndex={0} aria-checked={!thread} className={`v2-pen${thread ? "" : " v2-pen-on"}`} onClick={() => pick("ink")} onKeyDown={key("ink")}>Ink</span>
      <span role="radio" tabIndex={0} aria-checked={thread} aria-disabled={!can} className={`v2-pen v2-pen-thread${thread ? " v2-pen-on" : ""}${can ? "" : " v2-pen-out"}`}
        title={can ? "Thread: make something true. The world weaves something back." : "No threads left for now"}
        onClick={() => pick("thread")} onKeyDown={key("thread")}>
        Thread
        <span className="v2-knots" aria-label={`${weave.left} of ${weave.of} left`}>
          {Array.from({ length: weave.of }, (_, i) => <span key={i} className={`v2-knot${i < weave.left ? " v2-knot-on" : ""}`} aria-hidden="true" />)}
        </span>
      </span>
      <Tap className="v2-pen-help" label="How the two pens work" onTap={helpOnPens}>?</Tap>
      {thread && <span className="v2-pen-hint">Write what is true now.</span>}
    </p>
  );
}

/**
 * Once, above the line: the two pens and the pocket, for a player opening a story with V2 for the first time (a player
 * who played before is told it's new). "Show me" lights the pen switch; "How it works" opens the short list there.
 */
export function PensIntro({ s }: { s: StoryUI }) {
  const [due, setDue] = useState(introDue);
  const settled = !!s.id && !!s.view && !s.live && !s.pending && s.turns.length > 0;
  if (!due || !settled) return null;
  const done = () => { introSeen(); setDue(false); };
  const show = () => {
    done();
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(".v2-pens");
      el?.scrollIntoView({ block: "nearest" });
      el?.classList.add("v2-pens-show");
      window.setTimeout(() => el?.classList.remove("v2-pens-show"), 4500);
    });
  };
  return (
    <div className="v2-intro" role="note" aria-label="Two pens, and a pocket">
      <p><strong>{returning() ? "New: two pens, and a pocket." : "Two pens, and a pocket."}</strong>{" "}
        Above your line, <strong>Ink</strong> is what you do{returning() ? ", as before" : ""}. <strong>Thread</strong> makes
        something true: tap Thread, write “The ferryman owes me a favour,” and the world makes it so, and asks a price.
        Three a chapter.{" "}
        Tap a name in the story, then <strong>Pocket it</strong>, to keep that name above your line and write with it later.</p>
      <p className="v2-intro-go">
        <Tap className="v2-intro-act" onTap={show}>Show me</Tap>
        <Tap className="v2-intro-act" onTap={() => { done(); helpOnPens(); }}>How it works</Tap>
        <Tap className="v2-intro-act v2-intro-x" label="Got it, don't show this again" onTap={done}>got it</Tap>
      </p>
    </div>
  );
}

/** Under the line, the first time the Thread pen is picked: what a Thread line does. */
export function ThreadFirst({ s }: { s: StoryUI }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (s.pen === "ink") { setOn(false); return; }
    if (threadHintDue()) { threadHintSeen(); setOn(true); }
  }, [s.pen]);
  if (!on || s.pen !== "thread") return null;
  return <p className="v2-thread-first" role="note">Write something that's true now. The world will make it so, and ask a price.</p>;
}

/** Under a woven turn: what the world wove back, or that it wouldn't hold the thread (and gave it back). */
export function Pull({ s, n, className = "" }: { s: StoryUI; n: number; className?: string }) {
  const w = s.view?.turns.find((t) => t.n === n)?.weave;
  if (!w) return null;
  // An aside with its own paragraph, like the pull: the themes' rules for a reply's paragraphs don't reach it.
  if (w.refused) return <aside className={`v2-pull v2-pull-refused ${className}`} aria-label="The world refused the thread"><p>The world wouldn't hold that thread. It's yours again.</p></aside>;
  if (!w.pull) return null;
  return (
    <aside className={`v2-pull ${className}`} aria-label="The world pulls back">
      <span className="v2-pull-h">the world pulls back</span>
      <p>{w.pull}</p>
    </aside>
  );
}
